import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Plus, Eye } from "lucide-react";
import {
  useBankAccounts,
  useBankAccountById,
  useCreateBankAccount,
  useBankTransactions,
  useCreateBankTransaction,
  usePostableAccounts,
} from "../hooks/useAccounting";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import type { CreateBankAccountDto, CreateBankTransactionDto } from "../services/accountingApi";

function fmt(n: number) {
  return new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", minimumFractionDigits: 2 }).format(n);
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-KE", { year: "numeric", month: "short", day: "numeric" });
}

// ── Create Bank Account Dialog ────────────────────────────────────────────────

function CreateBankAccountDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: glAccounts } = usePostableAccounts();
  const createMutation = useCreateBankAccount();
  const notify = useNotification();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [form, setForm] = useState({
    accountName: "", accountNumber: "", bankName: "", branchName: "",
    branchCode: "", swiftCode: "", currency: "KES", glAccountId: "", openingBalance: 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const dto: CreateBankAccountDto = {
      ...form,
      branchName: form.branchName || null,
      branchCode: form.branchCode || null,
      swiftCode: form.swiftCode || null,
      glAccountId: form.glAccountId || null,
    };
    createMutation.mutate(dto, {
      onSuccess: () => { notify.success("Bank account created"); onOpenChange(false); },
      onError: (err) => {
        const fe = getFieldErrors(err);
        if (fe) setFieldErrors(fe);
        notify.error(getErrorMessage(err));
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>New Bank Account</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Account Name <span className="text-red-500">*</span></FieldLabel>
              <Input value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })} required />
              {fieldErrors.accountName && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountName.join(", ")}</p>}
            </Field>
            <Field>
              <FieldLabel>Account Number <span className="text-red-500">*</span></FieldLabel>
              <Input value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} required />
            </Field>
            <Field>
              <FieldLabel>Bank Name <span className="text-red-500">*</span></FieldLabel>
              <Input value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} required />
            </Field>
            <Field>
              <FieldLabel>Branch Name</FieldLabel>
              <Input value={form.branchName} onChange={(e) => setForm({ ...form, branchName: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel>Currency</FieldLabel>
              <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel>Opening Balance</FieldLabel>
              <Input type="number" step={0.01} value={form.openingBalance} onChange={(e) => setForm({ ...form, openingBalance: Number(e.target.value) })} />
            </Field>
          </div>
          <Field>
            <FieldLabel>GL Account (link to Chart of Accounts)</FieldLabel>
            <Select value={form.glAccountId || "none"} onValueChange={(v) => setForm({ ...form, glAccountId: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Select GL account" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {glAccounts?.filter((a) => !a.isGroup && a.accountTypeName === "Asset").map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Bank Account Detail Dialog ────────────────────────────────────────────────

function BankAccountDetailDialog({ bankAccountId, open, onOpenChange }: { bankAccountId: string | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: account } = useBankAccountById(bankAccountId || undefined);
  const { data: transactions, isLoading } = useBankTransactions(bankAccountId || undefined);
  const createTxnMutation = useCreateBankTransaction();
  const notify = useNotification();

  const [showAddTxn, setShowAddTxn] = useState(false);
  const [txnForm, setTxnForm] = useState({
    transactionDate: new Date().toISOString().split("T")[0],
    description: "", referenceNumber: "", debitAmount: 0, creditAmount: 0,
    chequeNumber: "", chequeDate: "",
  });

  const handleAddTxn = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bankAccountId) return;
    const dto: CreateBankTransactionDto = {
      bankAccountId,
      transactionDate: new Date(txnForm.transactionDate).toISOString(),
      valueDate: null,
      referenceNumber: txnForm.referenceNumber || null,
      description: txnForm.description || null,
      debitAmount: txnForm.debitAmount,
      creditAmount: txnForm.creditAmount,
      chequeNumber: txnForm.chequeNumber || null,
      chequeDate: txnForm.chequeDate ? new Date(txnForm.chequeDate).toISOString() : null,
    };
    createTxnMutation.mutate({ bankAccountId, dto }, {
      onSuccess: () => { notify.success("Transaction added"); setShowAddTxn(false); },
      onError: (err) => notify.error(getErrorMessage(err)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{account?.accountName || "Bank Account"}</DialogTitle>
        </DialogHeader>
        {account && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            <div><span className="text-muted-foreground">Bank</span><div>{account.bankName}</div></div>
            <div><span className="text-muted-foreground">Account #</span><div className="font-mono">{account.accountNumber}</div></div>
            <div><span className="text-muted-foreground">Balance</span><div className="font-semibold">{fmt(account.currentBalance)}</div></div>
            <div><span className="text-muted-foreground">Last Reconciled</span><div>{account.lastReconciledDate ? fmtDate(account.lastReconciledDate) : "Never"}</div></div>
          </div>
        )}

        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold">Transactions</h3>
          <PermissionGate permissions={["bank_accounts.create_transaction"]}>
            <Button size="sm" onClick={() => setShowAddTxn(!showAddTxn)}>
              <Plus className="h-4 w-4 mr-1" /> Add Transaction
            </Button>
          </PermissionGate>
        </div>

        {showAddTxn && (
          <form onSubmit={handleAddTxn} className="border rounded-lg p-4 space-y-3 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field>
                <FieldLabel>Date</FieldLabel>
                <DatePicker value={txnForm.transactionDate} onChange={(v) => setTxnForm({ ...txnForm, transactionDate: v })} />
              </Field>
              <Field>
                <FieldLabel>Description</FieldLabel>
                <Input value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel>Reference</FieldLabel>
                <Input value={txnForm.referenceNumber} onChange={(e) => setTxnForm({ ...txnForm, referenceNumber: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel>Debit (In)</FieldLabel>
                <Input type="number" step={0.01} min={0} value={txnForm.debitAmount || ""} onChange={(e) => setTxnForm({ ...txnForm, debitAmount: Number(e.target.value) })} />
              </Field>
              <Field>
                <FieldLabel>Credit (Out)</FieldLabel>
                <Input type="number" step={0.01} min={0} value={txnForm.creditAmount || ""} onChange={(e) => setTxnForm({ ...txnForm, creditAmount: Number(e.target.value) })} />
              </Field>
              <Field>
                <FieldLabel>Cheque #</FieldLabel>
                <Input value={txnForm.chequeNumber} onChange={(e) => setTxnForm({ ...txnForm, chequeNumber: e.target.value })} />
              </Field>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" type="button" onClick={() => setShowAddTxn(false)}>Cancel</Button>
              <Button size="sm" type="submit" disabled={createTxnMutation.isPending}>Add</Button>
            </div>
          </form>
        )}

        {isLoading ? (
          <div className="py-4 text-center text-muted-foreground">Loading transactions...</div>
        ) : !transactions?.length ? (
          <div className="py-4 text-center text-muted-foreground">No transactions yet</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Reference</TableHead>
                <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm">{fmtDate(t.transactionDate)}</TableCell>
                  <TableCell className="text-sm max-w-[150px] truncate">{t.description || "—"}</TableCell>
                  <TableCell className="text-sm font-mono">{t.referenceNumber || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{t.debitAmount > 0 ? fmt(t.debitAmount) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{t.creditAmount > 0 ? fmt(t.creditAmount) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmt(t.runningBalance)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{t.reconciliationStatusName}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function BankAccountsPage() {
  const { data: bankAccounts, isLoading } = useBankAccounts();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const totalBalance = bankAccounts?.reduce((s, a) => s + a.currentBalance, 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bank Accounts</h1>
          <p className="text-muted-foreground">Manage bank accounts, transactions and reconciliation</p>
        </div>
        <PermissionGate permissions={["bank_accounts.create"]}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New Bank Account
          </Button>
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-sm text-muted-foreground">Total Accounts</p>
            <p className="text-2xl font-bold">{bankAccounts?.length || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-sm text-muted-foreground">Total Balance</p>
            <p className="text-2xl font-bold">{fmt(totalBalance)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-sm text-muted-foreground">Active Accounts</p>
            <p className="text-2xl font-bold">{bankAccounts?.filter((a) => a.isActive).length || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : !bankAccounts?.length ? (
            <div className="text-center py-8 text-muted-foreground">No bank accounts configured</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead><TableHead>Bank</TableHead><TableHead>Account #</TableHead>
                  <TableHead>Currency</TableHead><TableHead className="text-right">Balance</TableHead>
                  <TableHead>Last Reconciled</TableHead><TableHead>Status</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {bankAccounts.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailId(a.id)}>
                    <TableCell className="font-medium">{a.accountName}</TableCell>
                    <TableCell>{a.bankName}</TableCell>
                    <TableCell className="font-mono text-sm">{a.accountNumber}</TableCell>
                    <TableCell>{a.currency}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmt(a.currentBalance)}</TableCell>
                    <TableCell className="text-sm">{a.lastReconciledDate ? fmtDate(a.lastReconciledDate) : "Never"}</TableCell>
                    <TableCell>{a.isActive ? <Badge className="bg-green-100 text-green-800">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateBankAccountDialog open={createOpen} onOpenChange={setCreateOpen} />
      <BankAccountDetailDialog bankAccountId={detailId} open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} />
    </div>
  );
}
